// dompurify and markdown-it are CommonJS `export =` modules; with esModuleInterop
// disabled in this tsconfig they are imported via `import = require(...)`, matching
// the repo convention (see jquery / node-htmldiff usages). This gives the real typed
// value directly — no namespace import or `unknown` cast needed.
import DOMPurify = require("dompurify");
import MarkdownIt = require("markdown-it");
import { full as markdownItEmoji } from "markdown-it-emoji";
import markdownItSub = require("markdown-it-sub");
import markdownItSup = require("markdown-it-sup");
import markdownItIns = require("markdown-it-ins");
import markdownItMark = require("markdown-it-mark");
import markdownItDeflist = require("markdown-it-deflist");
import markdownItAbbr = require("markdown-it-abbr");
import type { Editor as TinyMCEEditor } from "tinymce";

// Shared markdown-it instance. Default preset enables GFM tables + strikethrough.
// html:false keeps raw HTML in the pasted source out of the output; DOMPurify
// sanitizes the rendered result before it ever reaches the editor. typographer enables
// the smart-punctuation / (c)(r)(tm) replacements the premium markdown plugin applied.
const md = new MarkdownIt({
    html: false,
    linkify: true,
    breaks: false,
    typographer: true,
});

// Emoji support: converts :shortcode: (e.g. :wink:) and emoticon shortcuts (e.g. :-))
// to unicode emoji, matching the premium markdown plugin's behaviour.
md.use(markdownItEmoji);

// Subscript (H~2~O) and superscript (19^th^) support.
md.use(markdownItSub).use(markdownItSup);

// Inserted (++text++ -> <ins>) and marked/highlighted (==text== -> <mark>) text.
md.use(markdownItIns).use(markdownItMark);

// Definition lists (Term / : Definition -> <dl>) and abbreviations (*[HTML]: ...).
md.use(markdownItDeflist).use(markdownItAbbr);

// Open pasted links in a new tab (matching the editor's link_default_target)
// and set rel="noopener noreferrer" to prevent reverse tabnabbing. markdown-it
// emits no target/rel by default, so without this the ADD_ATTR below is a no-op.
const defaultLinkOpen = md.renderer.rules.link_open ||
    ((tokens, idx, options, _env, self) => self.renderToken(tokens, idx, options));
md.renderer.rules.link_open = (tokens, idx, options, env, self) => {
    tokens[idx].attrSet("target", "_blank");
    tokens[idx].attrSet("rel", "noopener noreferrer");
    return defaultLinkOpen(tokens, idx, options, env, self);
};

// Structural markdown signals strong enough to auto-trigger conversion.
// Inline-only cues (bold **..**, `code`) are intentionally excluded: they
// match ordinary prose too often and a false positive silently re-flows a
// plain-text paste. Those still render once a structural signal triggers
// conversion, and pure-inline markdown can be forced with Ctrl/Cmd+Shift+V.
const MARKDOWN_PATTERNS: RegExp[] = [
    /^#{1,6}\s+\S/m,                 // ATX headings: "# Heading"
    /^\s*[-*+]\s+\S/m,               // unordered list markers
    /^\s*\d+\.\s+\S/m,               // ordered list markers
    /^\s*>\s+\S/m,                   // blockquotes
    /```/,                           // fenced code blocks
    /^\s*\|.+\|\s*$/m,               // table rows
    /^\s*(-{3,}|\*{3,}|_{3,})\s*$/m, // horizontal rules
    /\[[^\]]+\]\([^)]+\)/,           // links: [text](url)
];

export const looksLikeMarkdown = (text: string): boolean =>
    MARKDOWN_PATTERNS.some(re => re.test(text));

// Render markdown → sanitized HTML ready to insert. ADD_ATTR keeps the
// target/rel set by the link_open renderer above from being stripped.
export const renderMarkdown = (text: string): string =>
    DOMPurify.sanitize(md.render(text), { ADD_ATTR: ["target", "rel"] });

// Derive plain text from an HTML clipboard payload, preserving line breaks at block
// boundaries. Used only on the explicit force-markdown path when the clipboard carries
// text/html but no text/plain, so a user's "paste as markdown" is honoured instead of
// silently falling through to the raw HTML. markdown-it is line-oriented (headings,
// lists, tables all need newlines), so we insert a newline for each block-closing tag
// and <br> before stripping the remaining markup — otherwise the tags would be removed
// and adjacent text nodes would concatenate into a single unstructured line. DOMPurify
// sanitizes first (neutralising any scripts/handlers) and again at the end to strip all
// tags down to text content.
const BLOCK_BOUNDARY_REGEX = /<\/(?:p|div|li|ul|ol|h[1-6]|tr|td|th|blockquote|pre|table|thead|tbody|section|article|header|footer|figure|figcaption)>|<(?:br|hr)\s*\/?>/gi;
const htmlToPlainText = (html: string): string => {
    const safe = DOMPurify.sanitize(html);
    const withBreaks = safe.replace(BLOCK_BOUNDARY_REGEX, "$&\n");
    const text = DOMPurify.sanitize(withBreaks, { ALLOWED_TAGS: [], ALLOWED_ATTR: [] });
    // Collapse runs of 3+ newlines left by nested block tags into a paragraph break.
    return text.replace(/\n{3,}/g, "\n\n").trim();
};

/**
 * Wire markdown-on-paste behaviour onto a TinyMCE editor instance. The editor runs
 * in open-source (free/GPL) mode where the premium powerpaste / markdown plugins are
 * absent, so this replicates their "paste markdown -> formatted HTML" behaviour.
 *
 * Behaviour:
 * - Ctrl/Cmd+Shift+V marks the next paste as "force markdown". We let the browser's
 *   native paste-as-plain-text fire the paste handler below rather than reading the
 *   clipboard again here — that keeps a single insertion path and avoids an async
 *   clipboard read that can fail silently or double-insert on key auto-repeat. The
 *   flag is a timestamp scoped tightly to the shortcut's own paste: it lives only for
 *   FORCE_MARKDOWN_WINDOW_MS and is cleared by the paste it triggers AND by any other
 *   (non-shortcut) keydown, so a shortcut that never produced its own paste can't
 *   straddle and hijack a later, unrelated Ctrl+V or right-click paste.
 * - On paste we read the native clipboard (not PastePreProcess) because by then
 *   plain-text markdown has already been wrapped in <p>/<br> and is lossy. We
 *   auto-detect via looksLikeMarkdown, or convert unconditionally when the force
 *   shortcut fired within the window just before this paste. Rich HTML paste
 *   (e.g. Word) falls through to TinyMCE's default handling — unless forced, in which
 *   case an html-only clipboard is coerced to text so the explicit request is honoured.
 */
export const setupMarkdownPaste = (editor: TinyMCEEditor): void => {
    // Short window: just long enough to bridge the shortcut keydown to the native
    // paste it triggers, without spanning a second, independent paste action.
    const FORCE_MARKDOWN_WINDOW_MS = 200;
    let forceMarkdownAt = 0;

    editor.on("keydown", (e: KeyboardEvent) => {
        const mod = e.ctrlKey || e.metaKey;
        if (mod && e.shiftKey && e.key?.toLowerCase() === "v") {
            forceMarkdownAt = Date.now();
        } else {
            // Any other keystroke means the shortcut (if it fired) did not lead to a
            // paste of its own — drop the flag so it can't apply to a later paste.
            forceMarkdownAt = 0;
        }
    });

    editor.on("paste", (e: ClipboardEvent) => {
        const force = Date.now() - forceMarkdownAt < FORCE_MARKDOWN_WINDOW_MS;
        forceMarkdownAt = 0;
        const cb = e.clipboardData || (window as unknown as { clipboardData?: DataTransfer }).clipboardData;
        if (!cb) return;
        const html = cb.getData("text/html");
        if (!force && html && html.trim()) return; // rich paste (e.g. Word) → default behavior
        // Prefer text/plain; on the force path fall back to text derived from text/html
        // so an html-only clipboard still honours the explicit "paste as markdown".
        let text = cb.getData("text/plain");
        if (!text && force && html && html.trim()) {
            text = htmlToPlainText(html);
        }
        if (!text || (!force && !looksLikeMarkdown(text))) return;
        e.preventDefault();
        editor.insertContent(renderMarkdown(text));
    });
};
